// Annual Vendor Audit — the vendor record's own history strip. Every audit
// ever run on this vendor, newest year first, INCLUDING superseded
// corrections. Row click opens the shared drawer, deep-linkable via
// ?tab=annual-audit&audit=<id>.
//
// This is the only surface that shows a correction chain. The registry page
// resolves the LIVE audit per vendor per year, so a superseded row is
// invisible there by design — the record is where "we got this wrong and
// re-ran it" belongs, next to the audit that replaced it.

import { useSearchParams } from "react-router-dom";
import {
  Card,
  StatusBadge,
  SubTable,
  TableValue,
  type SubTableColumn,
} from "@/design-system/components/ui";
import { useDateFormatter } from "@/utils/datetime";
import { useVendorAuditsForVendor } from "@/hooks/portfolioAdmin/useVendorAudits";
import type { VendorDto } from "@/api/portfolioAdmin/types";
import type { VendorAuditSummaryDto } from "@/api/portfolioAdmin/vendorAuditsApi";
import {
  VENDOR_AUDIT_DETERMINATION_LABEL,
  VENDOR_AUDIT_RECOMMENDATION_LABEL,
  VENDOR_AUDIT_STATUS_LABEL,
  VendorAuditDrawer,
  vendorAuditDeterminationTone,
  vendorAuditReviewClock,
  vendorAuditStatusTone,
} from "../audits/VendorAuditDrawer";

interface VendorAnnualAuditTabProps {
  vendor: VendorDto;
}

export function VendorAnnualAuditTab({ vendor }: VendorAnnualAuditTabProps) {
  const { date } = useDateFormatter();
  const [params, setParams] = useSearchParams();
  const openAuditId = params.get("audit");

  const auditsQuery = useVendorAuditsForVendor(vendor.id);

  const setAuditParam = (value: string | null) => {
    const updated = new URLSearchParams(params);
    if (value) updated.set("audit", value);
    else updated.delete("audit");
    setParams(updated, { replace: true });
  };

  const columns: SubTableColumn<VendorAuditSummaryDto>[] = [
    {
      key: "year",
      header: "Year",
      sortAccessor: (row) => row.auditYear,
      render: (row) => <span className="tabular-nums font-medium">{row.auditYear}</span>,
    },
    {
      key: "status",
      header: "Status",
      sortAccessor: (row) => row.status,
      render: (row) => {
        const clock = vendorAuditReviewClock(row);
        return (
          <span className="inline-flex items-center gap-space-1">
            <StatusBadge tone={vendorAuditStatusTone(row.status)}>
              {VENDOR_AUDIT_STATUS_LABEL[row.status]}
            </StatusBadge>
            {row.supersedesId && (
              <span className="text-caption text-gray-mid italic">correction</span>
            )}
            {clock && !clock.severity && (
              <span className="text-caption text-gray-mid">{clock.label}</span>
            )}
          </span>
        );
      },
    },
    {
      key: "determination",
      header: "Determination",
      sortAccessor: (row) => row.finalDetermination ?? "",
      render: (row) =>
        row.finalDetermination ? (
          <StatusBadge tone={vendorAuditDeterminationTone(row.finalDetermination)}>
            {VENDOR_AUDIT_DETERMINATION_LABEL[row.finalDetermination]}
          </StatusBadge>
        ) : (
          // Not a missing value — the determination is recorded at completion,
          // so before then there is nothing to record and nothing to do here.
          <span className="text-caption text-gray-mid">Not yet determined</span>
        ),
    },
    {
      key: "recommendation",
      header: "Recommendation",
      sortAccessor: (row) => row.finalRecommendation ?? "",
      render: (row) => (
        <TableValue
          value={
            row.finalRecommendation
              ? VENDOR_AUDIT_RECOMMENDATION_LABEL[row.finalRecommendation]
              : null
          }
        />
      ),
    },
    {
      key: "quotes",
      header: "Competitors",
      align: "right",
      sortAccessor: (row) => row.quoteCount,
      render: (row) => <span>{row.quoteCount}</span>,
    },
    {
      key: "completed",
      header: "Completed",
      sortAccessor: (row) => row.completedAt ?? "",
      render: (row) => <TableValue value={row.completedAt ? date(row.completedAt) : null} />,
    },
  ];

  if (auditsQuery.isLoading) {
    return (
      <Card>
        <p className="text-small text-gray-mid italic m-0">Loading audits…</p>
      </Card>
    );
  }
  if (auditsQuery.isError) {
    return (
      <Card>
        <p className="text-small text-status-error m-0" role="alert">
          Could not load this vendor&apos;s audits. Refresh to retry.
        </p>
      </Card>
    );
  }

  const rows = auditsQuery.data ?? [];

  return (
    <div className="flex flex-col gap-space-4" data-testid="vendor-annual-audit-tab">
      <Card>
        <div className="flex items-start justify-between gap-space-3 flex-wrap mb-space-3">
          <div>
            <h3 className="font-display font-bold text-h3 m-0">Annual audits</h3>
            <p className="text-small text-gray-mid m-0 mt-space-1">
              The yearly market check on this vendor. A completed audit is never
              edited — a correction supersedes it, and both stay on the record.
            </p>
          </div>
        </div>

        <SubTable
          caption="Annual audits for this vendor"
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          rowTestId={(row) => `vendor-audit-history-row-${row.id}`}
          onRowClick={(row) => setAuditParam(row.id)}
          rowSelected={(row) => row.id === openAuditId}
          data-testid="vendor-annual-audit-table"
          empty={{
            message:
              "No audit has been run on this vendor yet. Audits are opened from the Annual Audit registry.",
          }}
        />
      </Card>

      {openAuditId && (
        <VendorAuditDrawer
          auditId={openAuditId}
          open={!!openAuditId}
          onClose={() => setAuditParam(null)}
          onReplaced={(replacementId) => setAuditParam(replacementId)}
        />
      )}
    </div>
  );
}
